import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const IDLE_MS = 10 * 60 * 1000; // total inactivity before logout
const WARN_MS = 60 * 1000; // show countdown warning this long before logout
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [warning, setWarning] = useState(false);
  const [seconds, setSeconds] = useState(Math.floor(WARN_MS / 1000));
  const warningRef = useRef(false);
  const stayRef = useRef(() => {});

  useEffect(() => {
    if (!user) { setWarning(false); return; }

    let warnT, logoutT, tickT;
    const clearAll = () => { clearTimeout(warnT); clearTimeout(logoutT); clearInterval(tickT); };

    const doLogout = async () => {
      clearAll();
      sessionStorage.setItem("gk_idle_logout", "1");
      try { await logout(); } catch (e) { /* noop */ }
      const onAdmin = window.location.pathname.startsWith("/admin");
      navigate(onAdmin ? "/admin/login" : "/login", { replace: true });
    };

    const startCountdown = () => {
      warningRef.current = true;
      setWarning(true);
      let remaining = Math.floor(WARN_MS / 1000);
      setSeconds(remaining);
      tickT = setInterval(() => {
        remaining -= 1;
        setSeconds(remaining);
        if (remaining <= 0) clearInterval(tickT);
      }, 1000);
      logoutT = setTimeout(doLogout, WARN_MS);
    };

    const arm = () => {
      clearAll();
      warningRef.current = false;
      setWarning(false);
      warnT = setTimeout(startCountdown, IDLE_MS - WARN_MS);
    };

    // activity only extends the session while the warning is not showing
    const onActivity = () => { if (!warningRef.current) arm(); };

    stayRef.current = arm;
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    arm();

    return () => {
      clearAll();
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [user, logout, navigate]);

  if (!warning) return null;

  const logoutNow = async () => {
    sessionStorage.setItem("gk_idle_logout", "1");
    try { await logout(); } catch (e) { /* noop */ }
    const onAdmin = window.location.pathname.startsWith("/admin");
    navigate(onAdmin ? "/admin/login" : "/login", { replace: true });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink-950/70 backdrop-blur-sm" data-testid="idle-warning-modal">
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-7 text-center animate-in fade-in zoom-in-95 duration-200">
        <div className="w-14 h-14 rounded-2xl bg-brand/10 flex items-center justify-center mx-auto mb-4">
          <Clock className="w-7 h-7 text-brand" />
        </div>
        <h2 className="font-display text-xl font-extrabold tracking-tight text-ink-950">Still there?</h2>
        <p className="text-sm text-neutral-500 mt-2">
          You'll be logged out in{" "}
          <span className="font-bold text-brand tabular-nums" data-testid="idle-countdown">{Math.max(0, seconds)}</span>{" "}
          second{seconds === 1 ? "" : "s"} due to inactivity.
        </p>
        <div className="mt-6 space-y-2.5">
          <button data-testid="idle-stay-btn" onClick={() => stayRef.current()}
            className="w-full h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold transition-colors">
            Stay signed in
          </button>
          <button data-testid="idle-logout-btn" onClick={logoutNow}
            className="w-full h-11 rounded-full text-sm font-semibold text-neutral-500 hover:text-ink-900 transition-colors">
            Log out now
          </button>
        </div>
      </div>
    </div>
  );
}
